import React from "react";
import { Link } from "react-router-dom";
import Lottie from "lottie-react";
import internshipAnimation from "../assets/internship.json";
import { AcademicCapIcon, UserGroupIcon, CheckBadgeIcon } from "@heroicons/react/24/outline";

const Home = () => {
  return (
    <main
      role="main"
      className="relative min-h-screen text-gray-900 flex flex-col items-center justify-center px-6 py-16 sm:py-24 md:py-32 text-center overflow-hidden"
    >
      {/* Lottie Background */}
      <div
        className="fixed inset-0 -z-20 pointer-events-none opacity-20"
        aria-hidden="true"
      >
        <Lottie
          animationData={internshipAnimation}
          loop={true}
          style={{ width: "100%", height: "100%" }}
        />
      </div>

      <div
        className="fixed inset-0 -z-10 bg-black opacity-0 pointer-events-none"
        aria-hidden="true"
      />

      {/* Header */}
      <header className="mb-8 animate-bounce-slow relative z-10" role="banner">
        <img
          src="/Logo.png"
          alt="Techverra company logo"
          className="h-24 w-auto drop-shadow-md mx-auto"
          loading="lazy"
        />
      </header>

      {/* Intro Section */}
      <section className="relative z-10 mb-10" aria-labelledby="home-title">
        <h1
          id="home-title"
          className="text-indigo-600 font-extrabold mb-4 text-[clamp(2.5rem,4vw,3.5rem)] drop-shadow-sm tracking-wide"
        >
          TECHVERRA INTERNSHIPS
        </h1>
        <p className="text-gray-700 max-w-2xl mx-auto mb-12 text-lg leading-relaxed px-2 sm:px-0">
          Empowering students with real-world experience in{" "}
          <span className="text-indigo-600 font-semibold">Cybersecurity</span>,{" "}
          <span className="text-indigo-600 font-semibold">AI</span>,{" "}
          <span className="text-indigo-600 font-semibold">Web Development</span>, and more.
          Learn, build, and grow with our hands-on internship programs.
        </p>
      </section>

      {/* Call to Actions */}
      <section
        className="relative z-10"
        aria-labelledby="internship-actions"
      >
        <nav
          id="internship-actions"
          className="flex flex-col sm:flex-row flex-wrap justify-center gap-4 sm:gap-6"
          aria-label="Internship call-to-actions"
        >
          <Link
            to="/courses"
            aria-label="View available internships"
            className="bg-indigo-600 hover:bg-indigo-700 shadow-indigo-400/60 shadow-md px-6 py-3 rounded-xl text-white font-semibold transition transform duration-300 hover:scale-105 hover:shadow-lg focus:outline-none focus:ring-4 focus:ring-indigo-400 focus:ring-opacity-50"
          >
            📚 View Internships
          </Link>
          <Link
            to="/apply"
            aria-label="Apply for an internship"
            className="bg-indigo-300 hover:bg-indigo-400 shadow-indigo-300/60 shadow-md px-6 py-3 rounded-xl text-gray-900 font-semibold transition transform duration-300 hover:scale-105 hover:shadow-lg focus:outline-none focus:ring-4 focus:ring-indigo-300 focus:ring-opacity-50"
          >
            📝 Apply Now
          </Link>
        </nav>
      </section>

      {/* Features */}
      <section className="relative z-10 max-w-5xl mx-auto my-20 grid grid-cols-1 sm:grid-cols-3 gap-10 px-6" aria-label="Internship features">
        <article className="flex flex-col items-center text-center">
          <AcademicCapIcon className="w-12 h-12 mb-4 text-indigo-600" aria-hidden="true" />
          <h3 className="font-semibold mb-2">Real Projects</h3>
          <p>Work on real-world challenges to build your portfolio.</p>
        </article>
        <article className="flex flex-col items-center text-center">
          <UserGroupIcon className="w-12 h-12 mb-4 text-indigo-600" aria-hidden="true" />
          <h3 className="font-semibold mb-2">Mentorship</h3>
          <p>Get guided by industry experts every step of the way.</p>
        </article>
        <article className="flex flex-col items-center text-center">
          <CheckBadgeIcon className="w-12 h-12 mb-4 text-indigo-600" aria-hidden="true" />
          <h3 className="font-semibold mb-2">Certification</h3>
          <p>Earn recognized certificates to showcase your skills.</p>
        </article>
      </section>

      {/* FAQ */}
      <section className="relative z-10 max-w-3xl mx-auto my-20 px-6" aria-label="Frequently Asked Questions">
        <h2 className="text-2xl font-bold mb-8 text-indigo-600">Frequently Asked Questions</h2>
        <dl className="space-y-6 text-left text-gray-700">
          <div>
            <dt className="font-semibold">Who can apply?</dt>
            <dd className="mt-1">Students currently enrolled in undergraduate or graduate programs.</dd>
          </div>
          <div>
            <dt className="font-semibold">Are the internships paid?</dt>
            <dd className="mt-1">Some are paid; details are provided with each internship listing.</dd>
          </div>
          <div>
            <dt className="font-semibold">Can I do multiple internships?</dt>
            <dd className="mt-1">Yes! You can apply for multiple internships, but each has its own schedule.</dd>
          </div>
        </dl>
      </section>

      {/* Testimonials */}
      <section className="relative z-10 max-w-4xl mx-auto my-20 px-6 text-left" aria-label="Intern testimonials">
        <h2 className="text-2xl font-bold mb-8 text-indigo-600">What Our Interns Say</h2>
        <div className="space-y-8">
          <blockquote className="border-l-4 border-indigo-600 pl-6 italic text-gray-700">
            "Techverra internships gave me real industry experience that I couldn’t get anywhere else."
            <footer className="mt-2 font-semibold">— Ananya S.</footer>
          </blockquote>
          <blockquote className="border-l-4 border-indigo-600 pl-6 italic text-gray-700">
            "The mentorship and projects helped me secure a full-time job in Cybersecurity."
            <footer className="mt-2 font-semibold">— Rajesh K.</footer>
          </blockquote>
        </div>
      </section>

      {/* Animation CSS for bounce */}
      <style>{`
        @keyframes bounceSlow {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-10px); }
        }
        .animate-bounce-slow {
          animation: bounceSlow 3s ease-in-out infinite;
        }
      `}</style>
    </main>
  );
};

export default Home;
